import React, { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useHelper } from '@react-three/drei';
import * as THREE from 'three';

const FactoryScene = ({ tempData, pressureData, speedData }) => {
  const directionalLight = useRef();
  
  // Simulate machine data
  const [temp, setTemp] = useState(75);
  const [pressure, setPressure] = useState(4.0);
  const [speed, setSpeed] = useState(45);
  
  // Update values when props change
  useEffect(() => {
    if (tempData) setTemp(tempData);
    if (pressureData) setPressure(pressureData);
    if (speedData) setSpeed(speedData);
  }, [tempData, pressureData, speedData]);
  
  // Products on conveyor
  const products = useRef([]);
  const machineRef = useRef();
  const tempSensorRef = useRef();
  
  // Animation frame
  useFrame((state, delta) => {
    // Update products position based on speed
    products.current.forEach(product => {
      product.position.x += 0.03 * (speed / 40);
      if (product.position.x > 6) {
        product.position.x = -6;
      }
    });
    
    // Temperature sensor color based on temperature
    if (tempSensorRef.current) {
      const tempColor = new THREE.Color(Math.min(1, temp / 70), 0.1, 0.1);
      tempSensorRef.current.material.color = tempColor;
    }
  });

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.8} />
      <directionalLight 
        ref={directionalLight}
        position={[5, 10, 5]} 
        intensity={0.8}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      
      {/* Floor - Larger */}
      <mesh position={[0, -1, 0]} rotation={[Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[50, 50]} />
        <meshStandardMaterial color="#555555" roughness={0.8} metalness={0.2} side={THREE.DoubleSide} />
      </mesh>
      
      {/* Conveyor Base - Larger */}
      <mesh position={[0, 0, 0]} castShadow receiveShadow>
        <boxGeometry args={[18, 0.2, 3]} />
        <meshStandardMaterial color="#333333" roughness={0.7} metalness={0.3} />
      </mesh>
      
      {/* Machine Base */}
      <mesh ref={machineRef} position={[-4, 0.85, 0]} castShadow>
        <boxGeometry args={[2, 1.5, 2.5]} />
        <meshStandardMaterial color="#3366aa" roughness={0.5} metalness={0.6} />
      </mesh>
      
      {/* Machine Top */}
      <mesh position={[-4, 2.1, 0]} castShadow>
        <boxGeometry args={[1.5, 1, 2]} />
        <meshStandardMaterial color="#5588cc" roughness={0.3} metalness={0.8} />
      </mesh>
      
      {/* Control Panel */}
      <mesh position={[-3, 1.2, 1]} castShadow>
        <boxGeometry args={[0.8, 0.8, 0.2]} />
        <meshStandardMaterial color="#222222" roughness={0.2} metalness={0.8} />
      </mesh>
      
      {/* Panel Screen */}
      <mesh position={[-3, 1.2, 1.11]}>
        <planeGeometry args={[0.6, 0.4]} />
        <meshBasicMaterial color="#88ccff" />
      </mesh>
      
      {/* Temperature Sensor */}
      <mesh 
        ref={tempSensorRef} 
        position={[-4.5, 1.2, 0.8]} 
        rotation={[0, 0, Math.PI / 2]}
      >
        <cylinderGeometry args={[0.1, 0.1, 0.4, 8]} />
        <meshBasicMaterial color={new THREE.Color(Math.min(1, temp / 70), 0.1, 0.1)} />
      </mesh>
      
      {/* Products on conveyor - create multiple products */}
      {[...Array(8)].map((_, i) => (
        <mesh 
          key={i}
          ref={(el) => products.current[i] = el}
          position={[-5 + i * 1.5, 0.35, 0]} 
          castShadow
        >
          <boxGeometry args={[0.4, 0.3, 0.4]} />
          <meshStandardMaterial color="#22cc88" roughness={0.4} metalness={0.2} />
        </mesh>
      ))}
      
      {/* Quality Control Station */}
      <mesh position={[4, 0.7, 0]} castShadow>
        <boxGeometry args={[2, 1.2, 2.2]} />
        <meshStandardMaterial color="#99aacc" roughness={0.5} metalness={0.5} />
      </mesh>
    </>
  );
};

export default FactoryScene;